/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  Skeleton,
  Stack,
  type Theme,
  Typography,
} from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import type { AbsoluteURL, ListResultsQuery } from '@poweralpha/common';
import type { UseQueryResult } from '@tanstack/react-query';

import { motion } from 'framer-motion';

import type { HTTPError } from 'ky';
import { range } from 'lodash';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';

interface Props<T, IdKey extends keyof T> {
  label: string;
  queryResult: UseQueryResult<T[] | ListResultsQuery<T>, HTTPError>;
  getRow: (row: T) => React.ReactElement;
  getLink: (row: T) => AbsoluteURL | null;
  idKey: IdKey;
  customButtonSx?: (row: T) => (theme: Theme) => SystemStyleObject<Theme>;
  listItemHeight?: number;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  onMouseEnter?: (row: T) => void;
  onMouseLeave?: (row: T) => void;
  hasMore?: boolean;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: codex
function RecordList<T, IdKey extends keyof T>({
  label,
  queryResult,
  getRow,
  getLink,
  idKey,
  customButtonSx,
  listItemHeight = 58,
  onLoadMore,
  onMouseEnter,
  onMouseLeave,
  isLoadingMore = false,
  hasMore,
}: Readonly<Props<T, IdKey>>) {
  const rows = Array.isArray(queryResult.data) ? queryResult.data : (queryResult.data?.rows ?? []);
  const totalCount = Array.isArray(queryResult.data) ? undefined : queryResult.data?.count;
  const canLoadMore =
    hasMore ?? (typeof totalCount === 'number' ? totalCount > rows.length : false);
  const shouldShowLoadMore = canLoadMore && Boolean(onLoadMore);
  const shouldShowEndState = !canLoadMore && Boolean(onLoadMore || hasMore !== undefined);
  const listRef = useRef<HTMLUListElement | null>(null);
  const shouldRestoreScrollRef = useRef(false);
  const restoreScrollTopRef = useRef(0);
  const restoreMinRowCountRef = useRef(0);
  const loadMoreRequestedRef = useRef(false);
  const isFetchingMore = isLoadingMore || queryResult.isFetching;

  const handleLoadMore = useCallback(() => {
    if (!onLoadMore || !shouldShowLoadMore || isFetchingMore || loadMoreRequestedRef.current) {
      return;
    }

    if (listRef.current) {
      restoreScrollTopRef.current = listRef.current.scrollTop;
      restoreMinRowCountRef.current = rows.length;
      shouldRestoreScrollRef.current = true;
    }
    loadMoreRequestedRef.current = true;
    onLoadMore();
  }, [onLoadMore, shouldShowLoadMore, isFetchingMore, rows.length]);

  useEffect(() => {
    if (!isFetchingMore) {
      loadMoreRequestedRef.current = false;
    }
  }, [isFetchingMore]);

  useLayoutEffect(() => {
    if (!shouldRestoreScrollRef.current || !listRef.current) {
      return;
    }

    if (rows.length < restoreMinRowCountRef.current) {
      return;
    }

    listRef.current.scrollTop = restoreScrollTopRef.current;

    if (!isFetchingMore) {
      shouldRestoreScrollRef.current = false;
    }
  }, [rows.length, isFetchingMore]);

  if (queryResult.isLoading) {
    return (
      <List ref={listRef} dense disablePadding sx={{ maxHeight: 340, overflowY: 'auto' }}>
        {range(6).map((rowId) => (
          <ListItem key={`loading-record-${rowId}`} disablePadding sx={{ py: 0.25, px: 0 }}>
            <ListItemButton
              disabled
              sx={{
                borderRadius: 1,
                px: 0,
                py: 0.75,
                minHeight: listItemHeight,
                height: listItemHeight,
                '&.Mui-disabled': { opacity: 1 },
              }}
            >
              <Stack
                spacing={0.5}
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  px: 1.5,
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.75}
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Skeleton variant="text" width="58%" height={18} />
                  <Skeleton variant="rounded" width={62} height={18} />
                </Stack>
                <Skeleton variant="text" width="36%" height={16} />
              </Stack>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  }

  if (queryResult.isError) {
    return (
      <Box
        sx={{
          py: 2,
          px: 1.5,
        }}
      >
        <Typography variant="body2" sx={{ color: 'error.main' }}>
          Unable to load {label.toLowerCase()}s.
        </Typography>
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box
        sx={{
          py: 2,
          px: 1.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          No {label.toLowerCase()}s found.
        </Typography>
      </Box>
    );
  }

  let bottomStateContent: React.ReactNode = null;

  if (shouldShowLoadMore && onLoadMore) {
    bottomStateContent = (
      <ListItemButton
        onClick={handleLoadMore}
        disabled={isFetchingMore}
        sx={(theme) => ({
          borderRadius: 1,
          py: 0.55,
          minHeight: 34,
          justifyContent: 'center',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        })}
      >
        <Stack
          direction="row"
          spacing={0.8}
          sx={{
            alignItems: 'center',
          }}
        >
          {isFetchingMore ? <CircularProgress size={14} color="inherit" /> : null}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
            }}
          >
            {isFetchingMore ? 'Loading more...' : `Load more ${label.toLowerCase()}s`}
          </Typography>
        </Stack>
      </ListItemButton>
    );
  } else if (shouldShowEndState) {
    bottomStateContent = (
      <Box
        sx={(theme) => ({
          width: '100%',
          py: 0.85,
          px: 1.5,
          borderRadius: 1,
          textAlign: 'center',
          color: 'text.secondary',
          backgroundColor: theme.palette.action.hover,
        })}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
          }}
        >
          That&apos;s the end!
        </Typography>
      </Box>
    );
  }

  return (
    <List ref={listRef} dense disablePadding sx={{ maxHeight: 340, overflowY: 'auto' }}>
      {rows.map((row, index) => {
        const link = getLink(row);

        return (
          <ListItem
            component={motion.li}
            key={row[idKey] as string}
            initial={{ opacity: 0, x: -14 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{
              duration: 0.28,
              ease: 'easeOut',
              delay: Math.min(index, 10) * 0.04,
            }}
            sx={{ mb: index < rows.length - 1 ? 0.25 : 0, listStyle: 'none' }}
            disablePadding
          >
            <ListItemButton
              {...(link ? { component: RouterLink, to: link } : { component: 'div' as const })}
              onMouseEnter={() => onMouseEnter?.(row)}
              onMouseLeave={() => onMouseLeave?.(row)}
              sx={(theme) => ({
                position: 'relative',
                borderRadius: 1,
                minWidth: 0,
                px: 1.5,
                py: 0.75,
                minHeight: listItemHeight,
                height: listItemHeight,
                alignItems: 'flex-start',
                transition: 'background-color 120ms ease',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                ...customButtonSx?.(row)?.(theme),
              })}
            >
              <Box sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>{getRow(row)}</Box>
            </ListItemButton>
          </ListItem>
        );
      })}
      {(shouldShowLoadMore || shouldShowEndState) && (
        <ListItem disablePadding sx={{ pt: 0.75 }}>
          {bottomStateContent}
        </ListItem>
      )}
    </List>
  );
}

export default RecordList;
